import React, { useState, useEffect } from 'react';
import { userManagementApi } from '../../../services/userManagementApi';
import { User, CreateUserRequest, UpdateUserRequest, ResetPasswordRequest } from '../../../types/userManagement';

interface UserModalProps {
  user: User | null;
  mode: 'create' | 'edit' | 'view';
  availableRoles: string[];
  onSave: () => void;
  onClose: () => void;
}

const UserModal: React.FC<UserModalProps> = ({
  user,
  mode,
  availableRoles,
  onSave,
  onClose
}) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    nome: '',
    cognome: '',
    avatar: '',
    note: '',
    isAttivo: true,
    roles: [] as string[]
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email,
        password: '',
        nome: user.nome || '',
        cognome: user.cognome || '',
        avatar: user.avatar || '',
        note: user.note || '',
        isAttivo: user.isAttivo,
        roles: [...user.roles]
      });
    } else {
      // Reset per creazione nuovo utente
      setFormData({
        email: '',
        password: '',
        nome: '',
        cognome: '',
        avatar: '',
        note: '',
        isAttivo: true,
        roles: []
      });
    }
  }, [user]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRoleChange = (role: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      roles: checked 
        ? [...prev.roles, role]
        : prev.roles.filter(r => r !== role)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email.trim()) {
      setError('Email è obbligatoria');
      return;
    }

    if (mode === 'create' && !formData.password.trim()) {
      setError('Password è obbligatoria per un nuovo utente');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (mode === 'create') {
        const createRequest: CreateUserRequest = {
          email: formData.email.trim(),
          password: formData.password,
          nome: formData.nome.trim() || undefined,
          cognome: formData.cognome.trim() || undefined,
          avatar: formData.avatar.trim() || undefined,
          note: formData.note.trim() || undefined,
          roles: formData.roles
        };
        
        await userManagementApi.createUser(createRequest);
      } else if (mode === 'edit' && user) {
        const updateRequest: UpdateUserRequest = {
          email: formData.email.trim(),
          nome: formData.nome.trim() || undefined,
          cognome: formData.cognome.trim() || undefined,
          avatar: formData.avatar.trim() || undefined,
          note: formData.note.trim() || undefined,
          isAttivo: formData.isAttivo,
          roles: formData.roles
        };
        
        await userManagementApi.updateUser(user.id, updateRequest);
      }
      
      onSave();
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!user || !newPassword.trim()) {
      setError('Inserisci una nuova password');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const resetRequest: ResetPasswordRequest = {
        userId: user.id,
        newPassword: newPassword
      };
      
      await userManagementApi.resetPassword(resetRequest);
      setShowPasswordReset(false);
      setNewPassword('');
      alert('Password resettata con successo');
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const isReadOnly = mode === 'view';
  const isCreating = mode === 'create';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content user-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            {isCreating ? 'Nuovo Utente' : isReadOnly ? 'Dettagli Utente' : 'Modifica Utente'}
          </h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                disabled={isReadOnly}
                required
              />
            </div>
          </div>

          {isCreating && (
            <div className="form-row">
              <div className="form-group">
                <label>Password *</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  required
                  minLength={4}
                />
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>Nome</label>
              <input
                type="text"
                value={formData.nome}
                onChange={(e) => handleInputChange('nome', e.target.value)}
                disabled={isReadOnly}
              />
            </div>
            <div className="form-group">
              <label>Cognome</label>
              <input
                type="text"
                value={formData.cognome}
                onChange={(e) => handleInputChange('cognome', e.target.value)}
                disabled={isReadOnly}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Avatar (URL)</label>
              <input
                type="url"
                value={formData.avatar}
                onChange={(e) => handleInputChange('avatar', e.target.value)}
                disabled={isReadOnly}
                placeholder="https://esempio.com/avatar.jpg"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group full-width">
              <label>Note</label>
              <textarea
                value={formData.note}
                onChange={(e) => handleInputChange('note', e.target.value)}
                disabled={isReadOnly}
                rows={3}
              />
            </div>
          </div>

          {!isCreating && (
            <div className="form-row">
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.isAttivo}
                    onChange={(e) => handleInputChange('isAttivo', e.target.checked)}
                    disabled={isReadOnly}
                  />
                  Utente attivo
                </label>
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group full-width">
              <label>Ruoli</label>
              <div className="roles-grid">
                {availableRoles.map(role => (
                  <label key={role} className="role-checkbox">
                    <input
                      type="checkbox"
                      checked={formData.roles.includes(role)}
                      onChange={(e) => handleRoleChange(role, e.target.checked)}
                      disabled={isReadOnly}
                    />
                    {role}
                  </label>
                ))}
              </div>
            </div>
          </div>

          {user && mode === 'view' && (
            <div className="user-details">
              <div className="detail-row">
                <strong>ID:</strong> {user.id}
              </div>
              <div className="detail-row">
                <strong>Data Creazione:</strong> {new Date(user.dataCreazione).toLocaleString('it-IT')}
              </div>
              <div className="detail-row">
                <strong>Ultimo Accesso:</strong> {user.dataUltimoAccesso ? new Date(user.dataUltimoAccesso).toLocaleString('it-IT') : 'Mai'}
              </div>
              <div className="detail-row">
                <strong>Email Confermata:</strong> {user.emailConfirmed ? 'Sì' : 'No'}
              </div>
              {user.lockoutEnd && new Date(user.lockoutEnd) > new Date() && (
                <div className="detail-row">
                  <strong>Bloccato fino al:</strong> {new Date(user.lockoutEnd).toLocaleString('it-IT')}
                </div>
              )}
            </div>
          )}
        </form>

        <div className="modal-footer">
          {!isReadOnly && (
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
              onClick={handleSubmit}
            >
              {loading ? 'Salvando...' : (isCreating ? 'Crea' : 'Salva')}
            </button>
          )}
          
          {user && !isCreating && (
            <button
              type="button"
              className="btn btn-warning"
              onClick={() => setShowPasswordReset(!showPasswordReset)}
              disabled={loading}
            >
              Reset Password
            </button>
          )}
          
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            {isReadOnly ? 'Chiudi' : 'Annulla'}
          </button>
        </div>

        {/* Sezione Reset Password */}
        {showPasswordReset && user && (
          <div className="password-reset-section">
            <h4>Reset Password</h4>
            <div className="form-group">
              <label>Nuova Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                minLength={4}
                placeholder="Inserisci nuova password"
              />
            </div>
            <div className="reset-actions">
              <button
                type="button"
                className="btn btn-danger"
                onClick={handlePasswordReset}
                disabled={loading || !newPassword.trim()}
              >
                Conferma Reset
              </button>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => {
                  setShowPasswordReset(false);
                  setNewPassword('');
                }}
              >
                Annulla
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserModal;